import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { computeArea } from 'spherical-geometry-js';
import { Field, Polygon } from '../fields/field.model';
import { formatImportError, ImportBase, ParsedImportFile } from './import.service';
import { Feature, FeatureCollection, GeoJsonProperties, Geometry, Position } from 'geojson';
import shp from 'shpjs';
import { ZipArchive } from '@shortercode/webzip';

@Injectable({ providedIn: 'root' })
export class ImportShapefile extends ImportBase {
  override loadFields(files: File[]): Observable<ParsedImportFile[]> {
    return from(this.loadFilesInternal(files));
  }

  private async loadFilesInternal(files: File[]): Promise<ParsedImportFile[]> {
    const zipFiles = files.filter((file) => this.isZipFile(file));
    const singleFiles = files.filter((file) => !this.isZipFile(file));
    const parsedFiles = await Promise.all(
      zipFiles.map((zipFile) => this.loadFieldsFromZip(zipFile)),
    );

    if (singleFiles.length > 0) {
      const combinedSingleFiles = await this.createZipFromSingleShapefiles(singleFiles);
      parsedFiles.push(await this.loadFieldsFromZip(combinedSingleFiles));
    }

    return parsedFiles;
  }

  private async createZipFromSingleShapefiles(singleFiles: File[]) {
    const zip = new ZipArchive();

    for (const file of singleFiles) {
        await zip.set(file.name, file);
    } 

    const zipBlob = zip.to_blob();

    return new File([zipBlob], "single_files");
  }

  private async loadFieldsFromZip(file: File): Promise<ParsedImportFile> {
    try {
      const buff = await file.arrayBuffer();
      const geojson = await shp.parseZip(buff);
      const fields = Array.isArray(geojson)
        ? geojson.flatMap((gjson) => this.processShapeFile(gjson))
        : this.processShapeFile(geojson);

      return {
        filename: file.name,
        fields: fields
      };
    } catch (error) {
      return {
        filename: file.name,
        errorMessage: formatImportError(error),
      };
    }
  }

  private processShapeFile(
    geojson: FeatureCollection & {fileName?: string | undefined},
    fieldName?: string
  ): Field[] {
    const fields: Field[] = [];

    geojson.features.forEach((partfield) => {
      const geoInfo = this.getGeoInformation(partfield.geometry);

      //create new Field based off partfield
      const newField = new Field();
      newField.name =
        fieldName ?? this.findFieldName(partfield) ?? geojson.fileName ?? 'unknown'; //Schlagname maybe individuall?
      newField.area = geoInfo?.area || 0; // check if we want to have result in ha or in m²
      newField.polygon = new Polygon(geoInfo?.coordinates || []);

      newField.isSelected = false;
      fields.push(newField);
    });
    return fields;
  }

  getGeoInformation(geometry: Geometry) {
    const fieldCoordinates: Position[][] = [];
    let area: number = 0;

    //only working for polygons aka fields
    if (geometry.type != 'Polygon') {
      return; // skip this partfield
    }

    for (
      let areaIndex = 0;
      areaIndex < geometry.coordinates.length;
      areaIndex++
    ) {
      const areaCoordinates: Position[] = [];
      const geoCoordinates: { lat: number; lng: number }[] = [];

      geometry.coordinates[areaIndex].forEach((point) => {
        areaCoordinates.push(point);
        const pNorth: number = point[0];
        const pEast: number = point[1];
        geoCoordinates.push({ lat: pNorth, lng: pEast });
      });
      fieldCoordinates.push(areaCoordinates);

      // check if we want to have result in ha or in m²
      if (areaIndex == 0) {
        area += computeArea(geoCoordinates) / 10000;
      } else {
        area -= computeArea(geoCoordinates) / 10000; //TODO: exclusions could overlapp!!!
      }
    }

    return { coordinates: fieldCoordinates, area: area };
  }

  // search for field name in shape properties, use filename as fallback
  findFieldName(partfield: Feature<Geometry, GeoJsonProperties>) {
    //TODO: Find better solution that will work with more flexible
    const props = Object.keys(partfield.properties!) || [];
    if (props.includes('Schlagname')) {
      return partfield.properties!['Schlagname'];
    } else if (props.includes('name')) {
      return partfield.properties!['name'];
    }
    return undefined;
  }
}
